import { motion, useScroll, useTransform } from "framer-motion";
import { Compass, MoonStar, ShieldCheck } from "lucide-react";
import LeadForm from "./LeadForm";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
};

export default function Hero() {
  const { scrollY } = useScroll();
  const blobY1 = useTransform(scrollY, [0, 700], [0, 120]);
  const blobY2 = useTransform(scrollY, [0, 700], [0, -90]);

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      <motion.div
        style={{ y: blobY1 }}
        className="pointer-events-none absolute -top-32 right-0 h-[28rem] w-[28rem] rounded-full bg-sky-200/40 blur-3xl"
      />
      <motion.div
        style={{ y: blobY2 }}
        className="pointer-events-none absolute -bottom-40 -left-24 h-96 w-96 rounded-full bg-blue-200/30 blur-3xl"
      />

      <div className="relative mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
        <motion.img
          src="/assets/banner.jpg"
          alt="NowLifeWorks — Master Your Life. Grow With Purpose. Protect What Matters. Coaching, sleep and recovery, and legal protection from John Jones."
          data-testid="hero-banner"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="mx-auto mb-8 w-full max-w-3xl rounded-xl shadow-md"
        />
        <h1
          data-testid="hero-headline"
          className="text-center font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-6xl lg:text-7xl"
        >
          <span className="block overflow-hidden pb-2">
            <motion.span
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
              className="block"
            >
              Unlock{" "}
              <span className="bg-gradient-to-r from-blue-700 to-sky-500 bg-clip-text text-transparent">
                Peak Performance
              </span>
            </motion.span>
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="mt-4 text-center font-display text-lg font-semibold tracking-tight text-blue-800 sm:text-2xl"
        >
          Master Your Energy. Grow Your Life. Protect What You Build.
        </motion.p>

        <div className="mt-10 grid items-start gap-10 lg:mt-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.55, ease: "easeOut" }}
            className="order-2 scroll-mt-24 lg:order-1"
            id="about"
            data-testid="hero-about"
          >
            <div className="flex items-center gap-5">
              <img
                src="/assets/john-portrait.png"
                alt="John Jones"
                data-testid="hero-about-photo"
                className="w-28 shrink-0 rounded-2xl bg-black object-contain object-bottom shadow-lg sm:w-36"
              />
              <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                About John
              </h2>
            </div>
            <div className="mt-5 max-w-xl space-y-4 text-sm leading-relaxed text-slate-600 sm:text-base">
              <p data-testid="hero-subheadline">
                I'm John Jones — a Certified LifeWorks Life Mastery Coach, a NuCalm partner, and an
                independent LegalShield associate. I've spent my career helping people who feel
                stuck or stretched thin rebuild their energy, sharpen their focus, and put real
                protection around what they're building.
              </p>
              <p>
                Everything I recommend here is something I personally use. No hype, no hustle
                culture — just a system that works when you work it.
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              {[
                { icon: Compass, label: "Coaching System", href: "#coaching", testid: "chip-coaching" },
                { icon: MoonStar, label: "Sleep & Recovery", href: "#sleep-recovery", testid: "chip-sleep-recovery" },
                { icon: ShieldCheck, label: "Legal Protection", href: "#legal-protection", testid: "chip-legal-protection" },
              ].map(({ icon: Icon, label, href, testid }) => (
                <a
                  key={label}
                  href={href}
                  data-testid={testid}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-300 hover:text-blue-700 hover:shadow-md"
                >
                  <Icon className="h-4 w-4 text-blue-600" />
                  {label}
                </a>
              ))}
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.25, ease: "easeOut" }}
            id="hero-form"
            data-testid="hero-lead-card"
            className="order-1 rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl shadow-blue-900/10 sm:p-8 lg:order-2"
          >
            <div className="flex items-center gap-4">
              <img
                src="/assets/book-cover.png"
                alt="The Change 17 — Insights Into Self Empowerment, featuring John Jones"
                data-testid="ebook-cover"
                className="h-24 w-auto shrink-0 rounded-md shadow-md"
              />
              <div>
                <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">
                  Get the Free eBook
                </h2>
                <p className="mt-1 text-sm leading-snug text-slate-500">
                  <span className="font-semibold text-slate-700">
                    The Change ¹⁷: Insights Into Self Empowerment
                  </span>{" "}
                  — featuring John Jones, foreword by Les Brown.
                </p>
              </div>
            </div>
            <div className="mt-5">
              <LeadForm />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
